import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Authentication } from 'src/utils/authentication/Authentication';
import { ai } from '@sekoia/shared/utils/telemetryService';
import PinView from 'src/react/Pin/View/PinView';
import { MessageDialog } from 'src/react/_Layout/MessageDialog';
import { pinZIndex } from 'src/utils/zIndexConstants';
import PinLoader from './PinLoader';
import currentProfileProvider from 'src/utils/CurrentProfileProvider/CurrentProfileProvider';
import { roles } from '@sekoia/shared/domain/User/roles';

type PinWrapperProps = {
  onPinAccepted: () => Promise<void>;
  onPinSuccess?: () => void;
};

const PinWrapper: React.FC<PinWrapperProps> = (props) => {
  const { onPinAccepted, onPinSuccess } = props;
  const { t } = useTranslation(['pin', 'global']);
  const [pinEntered, setPinEntered] = useState('');
  const [isLoadingOnPinEntered, setIsLoadingOnPinEntered] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [wrongPinEnteredCount, setWrongPinEnteredCount] = useState(0);

  const profile = useMemo(() => currentProfileProvider.Instance.getCurrentProfile(), []);
  const isResident = useMemo(() => profile?.roles.some((r) => r === roles.citizenUser.name), [profile?.roles]);

  const name = useMemo(() => (profile ? profile.fullName : ''), [profile]);

  const avatarPicUrl = localStorage.getItem('profilePicUrl') ?? undefined;

  const callPinService = useCallback(
    async (enteredPin: string) => {
      setIsLoadingOnPinEntered(true);
      try {
        const status = await Authentication.Instance.pinSignin(enteredPin);

        if (status === 200) {
          await onPinAccepted();
          setPinEntered('');
          setWrongPinEnteredCount(0);
          onPinSuccess?.();
        } else if (status === 403) {
          setPinEntered('');
          setWrongPinEnteredCount((prev) => prev + 1);
          setShowErrorMessage(true);
        } else if (status === 401) {
          ai.trackTrace('PinWrapper, logout, status == 401');
          setIsSessionExpired(true);
        }
      } finally {
        setIsLoadingOnPinEntered(false);
      }
    },
    [onPinAccepted, onPinSuccess],
  );

  const onPinDigitDeleted = useCallback((action?: 'all') => {
    if (action === 'all') {
      setPinEntered('');
    } else {
      setPinEntered((prev) => prev.slice(0, prev.length - 1));
    }
  }, []);

  const onPinDigitEntered = useCallback(
    (input: string) => {
      setPinEntered((prev) => {
        if (prev.length < 4) {
          const newVal = prev + input;
          if (newVal.length === 4) {
            callPinService(newVal);
          }
          return newVal;
        }
        return prev;
      });
    },
    [callPinService],
  );

  const handleCloseErrorDialog = useCallback(() => {
    setShowErrorMessage(false);
  }, []);

  const handleSessionExpired = useCallback(() => {
    Authentication.Instance.logout();
  }, []);

  if (isResident) {
    Authentication.Instance.logout();
    return null;
  }

  return (
    <>
      <PinView
        enteredPin={pinEntered}
        onPinDigitEntered={onPinDigitEntered}
        onPinDigitDeleted={onPinDigitDeleted}
        wrongPinEnteredCount={wrongPinEnteredCount}
        avatarPicUrl={avatarPicUrl}
        name={name}
        showChangeUserButton={true}
        dialogZIndex={pinZIndex}
      />
      <MessageDialog
        open={showErrorMessage || isSessionExpired}
        title={isSessionExpired ? t('pin:sessionExpiredTitle') : t('pin:wrongPinTitle')}
        message={isSessionExpired ? t('pin:sessionExpiredMsg') : t('pin:wrongPinMsg')}
        onClose={isSessionExpired ? handleSessionExpired : handleCloseErrorDialog}
        buttonText={isSessionExpired ? t('global:ok') : undefined}
      />
      {isLoadingOnPinEntered && <PinLoader />}
    </>
  );
};

PinWrapper.displayName = 'PinWrapper';
export default memo(PinWrapper);
